import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { formatDateToMMDDYYYY, getMonthName } from '@utils/dates/dateMethods';
import { getCurrency } from '@utils/tools/currencyTools';
import { Table, Tooltip } from 'app-components';
import { IEnrollment } from '../common/types';

const StyledSection = styled.section`
    margin: 20px 0;

    table {
        tr {
            border: ${({ theme }) => `1px solid ${theme.gray8}`};
        }
        tr:nth-child(odd) {
            background-color: ${({ theme }) => ` ${theme.white}`};
        }

        tr:nth-child(even) {
            background-color: ${({ theme }) => theme.gray11};
        }
    }
`;

const StyledHeading = styled.h5`
    padding: 1rem 0;
`;

interface IEnrollmentPremiumHistoryTable {
    enrollment: IEnrollment;
    heading?: string;
}

const EnrollmentPremiumHistoryTable: React.FC<IEnrollmentPremiumHistoryTable> = ({ enrollment, heading }) => {
    const intl = useIntl();
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};

    const enrollmentBenefitEffectiveStartMonth =
        new Date(enrollment.enrollmentBenefitEffectiveStartDate).getMonth() + 1;
    const enrollmentBenefitEffectiveEndMonth = new Date(enrollment.enrollmentBenefitEffectiveEndDate).getMonth() + 1;

    const isWithinEffectiveRange = (month: number) =>
        month >= enrollmentBenefitEffectiveStartMonth &&
        month <= enrollmentBenefitEffectiveEndMonth &&
        enrollment.enrollmentBenefitEffectiveStartDateUI !== enrollment.enrollmentBenefitEffectiveEndDateUI;

    const getColumnDefination = useMemo(() => {
        const columnHeaders = [
            {
                titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.month' }),
                key: 'month',
                render: (premium: { month: number }) => <span>{getMonthName(premium.month)}</span>,
            },
        ];

        const addColumn = (condition: boolean, column: any) => {
            if (condition) columnHeaders.push(column);
        };

        const isNotApplicable = (premium: { month: number }) => {
            return !isWithinEffectiveRange(premium.month);
        };

        const showPremiumValue = (premium: { month: number }) =>
            isWithinEffectiveRange(premium.month) &&
            formatDateToMMDDYYYY(enrollment.enrollmentBenefitEffectiveStartDate) !==
                formatDateToMMDDYYYY(enrollment.enrollmentBenefitEffectiveEndDate);

        const premiumValue = premium => (showPremiumValue(premium) ? getCurrency(premium.maxAptc) : '');

        const electedAPTCValue = premium =>
            showPremiumValue(premium) && premium.isAptcEligible ? getCurrency(premium.aptcAmount) : '';

        const electedStateSubsidyValue = premium =>
            showPremiumValue(premium) && premium.isStateSubsidyEligible ? getCurrency(premium.stateSubsidyAmount) : '';

        const getPremiumType = premium => {
            if (isNotApplicable(premium)) {
                return '';
            }
            if (showPremiumValue(premium)) {
                return premium.isFinancial ? 'Financial' : 'Non-Financial';
            }
            return '';
        };

        const applicationTypeValue = premium => getPremiumType(premium);

        const calculateEhbPremiumValue = premium => {
            if (!showPremiumValue(premium)) {
                return '';
            }
            const { ehbPercentage } = enrollment;
            if (ehbPercentage !== null && ehbPercentage > 0 && ehbPercentage <= 1) {
                return getCurrency(premium.grossPremiumAmount * ehbPercentage);
            }
            return getCurrency(premium.grossPremiumAmount);
        };

        const ehbPremiumValue = premium => calculateEhbPremiumValue(premium);

        const columns = [
            {
                condition:
                    userPermissions?.memberEnrlmntGroupMaxAptc || userPermissions?.memberEnrlmntGroupMaxAptcEditable,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.grossPremium' }),
                    key: 'grossPremium',
                    render: (premium: { month: number; grossPremiumAmount: number }) => (
                        <>
                            {isNotApplicable(premium) ? (
                                <Tooltip
                                    id="member.enrollment_list.premiumhistory.notApplicable"
                                    elementId="premiumhistory"
                                    content="member.enrollment_list.premiumhistory.notApplicable.popup"
                                    place="top"
                                    dataAutomationId="coinsurance_tooltip"
                                />
                            ) : (
                                <>{getCurrency(premium.grossPremiumAmount)}</>
                            )}
                        </>
                    ),
                },
            },
            {
                condition:
                    userPermissions?.memberEnrlmntGroupMaxAptc || userPermissions?.memberEnrlmntGroupMaxAptcEditable,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.groupMaxAPTC' }),
                    key: 'groupMaxAPTC',
                    render: (premium: { month: number; maxAptc: number; isAptcEligible: boolean }) =>
                        premiumValue(premium),
                },
            },
            {
                condition: userPermissions?.memberEnrlmntAptc || userPermissions?.memberEnrlmntAptcEditable,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.electedAPTC' }),
                    key: 'electedAPTC',
                    render: (premium: { month: number; isAptcEligible: boolean; aptcAmount: number }) =>
                        electedAPTCValue(premium),
                },
            },
            {
                condition:
                    enrollment.isStateSubsidyEnabled &&
                    (userPermissions?.memberEnrlmntGroupMaxAptc || userPermissions?.memberEnrlmntGroupMaxAptcEditable),
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.groupMaxStateSubsidy' }),
                    key: 'groupMaxStateSubsidy',
                    render: (premium: { month: number; maxStateSubsidy: number }) => (
                        <>{showPremiumValue(premium) ? getCurrency(premium.maxStateSubsidy) : ''}</>
                    ),
                },
            },
            {
                condition:
                    enrollment.isStateSubsidyEnabled &&
                    (userPermissions?.memberEnrlmntAptc || userPermissions?.memberEnrlmntAptcEditable),
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.electedStateSubsidy' }),
                    key: 'electedStateSubsidy',
                    render: (premium: { month: number; isStateSubsidyEligible: boolean; stateSubsidyAmount: number }) =>
                        electedStateSubsidyValue(premium),
                },
            },
            {
                condition: enrollment.isStateSubsidy2Enabled,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.groupMaxStateSubsidy2' }),
                    key: 'groupMaxStateSubsidy',
                    render: (premium: { month: number; maxStateSudsidy2: number }) => (
                        <>
                            {showPremiumValue(premium) && premium?.maxStateSudsidy2
                                ? getCurrency(premium?.maxStateSudsidy2 || 0)
                                : ''}
                        </>
                    ),
                },
            },
            {
                condition: enrollment.isStateSubsidy2Enabled,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.electedStateSubsidy2' }),
                    key: 'electedStateSubsidy',
                    render: (premium: { month: number; stateSubsidy2Amount: number }) => (
                        <>
                            {showPremiumValue(premium) && premium?.stateSubsidy2Amount
                                ? getCurrency(premium?.stateSubsidy2Amount || 0)
                                : ''}
                        </>
                    ),
                },
            },
            {
                condition: enrollment.statePremiumCreditAmt,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.premiumCredit' }),
                    key: 'premiumCredit',
                    render: (premium: { month: number; statePremiumCreditAmt: number }) => (
                        <>{showPremiumValue(premium) ? getCurrency(premium.statePremiumCreditAmt) : ''}</>
                    ),
                },
            },
            {
                condition: enrollment.isMedicaidTransitionEnabled,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.mtpr' }),
                    key: 'medicaidTransitionAmount',
                    render: (premium: { month: number; medicaidTransitionAmount: number }) => (
                        <>{getCurrency(premium?.medicaidTransitionAmount) || ''}</>
                    ),
                },
            },
            {
                condition: true,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.netPremium' }),
                    key: 'netPremium',
                    render: (premium: { month: number; netPremiumAmount: number }) => (
                        <>{showPremiumValue(premium) ? getCurrency(premium.netPremiumAmount) : ''}</>
                    ),
                },
            },
            {
                condition: userPermissions?.memberEnrlmntAppType,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.applicationType' }),
                    key: 'applicationType',
                    render: (premium: { month: number; isFinancial: boolean }) => <>{applicationTypeValue(premium)}</>,
                },
            },
            {
                condition: true,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.slcsp' }),
                    key: 'slcsp',
                    render: (premium: { month: number; slcspPremiumAmount: number }) => (
                        <>{showPremiumValue(premium) ? getCurrency(premium.slcspPremiumAmount) : ''}</>
                    ),
                },
            },
            {
                condition: userPermissions?.memberEnrlmntEhbprmum || userPermissions?.memberEnrlmntEhbPrmumEditable,
                column: {
                    titleId: `${intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.ehbPremium' })} (${
                        enrollment.ehbPercentage != null && enrollment.ehbPercentage !== 0
                            ? enrollment.ehbPercentage > 1
                                ? enrollment.ehbPercentage
                                : +(enrollment.ehbPercentage * 100).toFixed(2)
                            : 0
                    } %)`,
                    key: 'ehbPremium',
                    render: (premium: { month: number; grossPremiumAmount: number }) => <>{ehbPremiumValue(premium)}</>,
                },
            },
        ];

        columns.forEach(({ condition, column }) => addColumn(condition, column));

        return columnHeaders;
    }, [enrollment, userPermissions, intl]);

    const enrollmentPremiumDtoList = useMemo(() => {
        return enrollment?.enrollmentPremiumDtoList?.sort((a, b) => a.month - b.month) || [];
    }, [enrollment.enrollmentPremiumDtoList]);

    return (
        <StyledSection className="enrollment-premium-view">
            <ConditionalRendering isTrue={!!heading}>
                <StyledHeading>{heading}</StyledHeading>
            </ConditionalRendering>

            <Table columns={getColumnDefination} data={enrollmentPremiumDtoList} />
        </StyledSection>
    );
};

export default EnrollmentPremiumHistoryTable;
